'use client'

import { memo } from 'react'
import { Badge } from '@/components/ui/badge'
import { Check, Minus } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'
export const ENTERPRISE_COMPARISON_ROWS = [
  { feature: 'Complete Data Sovereignty', cloud: false, local: true, ninx: true },
  { feature: 'Complex Reasoning Capability', cloud: true, local: false, ninx: true },
  { feature: 'Agentic Workflow Execution', cloud: false, local: false, ninx: true },
  { feature: 'Native Hardware Synergies', cloud: false, local: false, ninx: true },
  { feature: 'Predictable Operating Costs', cloud: false, local: true, ninx: true },
] as const

const ComparisonMark = ({ value, highlight }: { value: boolean; highlight?: boolean }) => {
  if (value) {
    return (
      <Check
        className={cn('mx-auto h-4 w-4', highlight ? 'text-[#21728d]' : 'text-emerald-600')}
        aria-label="Yes"
      />
    )
  }
  return <Minus className="mx-auto h-4 w-4 text-slate-300" aria-label="No" />
}

export const NinxlEnterpriseComparisonTable = memo(function NinxlEnterpriseComparisonTable({
  className,
}: {
  className?: string
}) {
  const winCount = ENTERPRISE_COMPARISON_ROWS.filter((row) => row.ninx).length

  return (
    <div className={cn('space-y-4', className)}>
      <div className="flex flex-wrap items-center justify-center gap-3 rounded-2xl border border-[#21728d]/15 bg-[#21728d]/5 px-4 py-3">
        <span className="text-sm font-semibold text-[#134b5f]">
          NINX Hybrid wins on {winCount} of {ENTERPRISE_COMPARISON_ROWS.length} enterprise capabilities
        </span>
        <div className="flex gap-1">
          {ENTERPRISE_COMPARISON_ROWS.map((row) => (
            <div
              key={row.feature}
              className={cn('h-2 w-2 rounded-full', row.ninx ? 'bg-[#21728d]' : 'bg-[#88a1ac]/30')}
              title={row.feature}
            />
          ))}
        </div>
      </div>

      <div className="ninxl-comparison-table overflow-x-auto rounded-3xl border border-slate-200 bg-white shadow-lg shadow-slate-200/50">
        <Table className="min-w-[720px]">
          <TableHeader>
            <TableRow className="border-b border-slate-100 hover:bg-transparent">
              <TableHead className="w-[28%] py-4 pl-6 text-xs font-normal text-slate-500">Feature</TableHead>
              <TableHead className="w-[18%] py-4 text-center text-xs font-normal text-slate-500">Cloud-First</TableHead>
              <TableHead className="w-[18%] py-4 text-center text-xs font-normal text-slate-500">Local-Only</TableHead>
              <TableHead className="ninxl-comparison-col-highlight w-[28%] py-4 pr-6 text-center">
                <span className="inline-flex items-center gap-2 font-semibold text-[#134b5f]">
                  NINX Hybrid
                  <Badge className="border-0 bg-[#21728d] px-2.5 text-[10px] tracking-wider text-white">
                    RECOMMENDED
                  </Badge>
                </span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {ENTERPRISE_COMPARISON_ROWS.map((row) => (
              <TableRow key={row.feature} className="border-b border-slate-50 hover:bg-[#f6f9fd]/50">
                <TableCell className="py-4 pl-6 font-medium text-slate-800">{row.feature}</TableCell>
                <TableCell className="py-4 text-center">
                  <ComparisonMark value={row.cloud} />
                </TableCell>
                <TableCell className="py-4 text-center">
                  <ComparisonMark value={row.local} />
                </TableCell>
                <TableCell className="ninxl-comparison-col-highlight py-4 pr-6 text-center">
                  <ComparisonMark value={row.ninx} highlight />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  )
})